import { useState, useEffect, useMemo } from 'react';
import { getSummaryHotEventStatsComments } from '@op/services/summary';
import { arrayToObj } from '@op/utils';
import { IStatsCommentsData, TContentKind } from '../types';

interface IUseEventDetailStatsProps {
  /** 当前选中事件的 id（用作 stats_comments 的 event_id） */
  eventId: number | undefined;
  /** 请求参数（gameInfo + filter 等，含 start_time/end_time/task_id） */
  filterParams?: Record<string, any>;
}

interface IUseEventDetailStatsReturn {
  statsData: IStatsCommentsData | null;
  contentsByKind: Record<TContentKind, { mentions: number; contents: any[] }>;
  loading: boolean;
}

const EMPTY_CONTENTS_BY_KIND: Record<
  TContentKind,
  { mentions: number; contents: any[] }
> = {
  Positive: { mentions: 0, contents: [] },
  Neutral: { mentions: 0, contents: [] },
  Negative: { mentions: 0, contents: [] },
};

/**
 * 右侧详情面板数据 Hook — 根据 eventId 调用 getSummaryHotEventStatsComments
 * start_time / end_time / task_id 已包含在 filterParams 中
 */
export default function useEventDetailStats({
  eventId,
  filterParams,
}: IUseEventDetailStatsProps): IUseEventDetailStatsReturn {
  const [statsData, setStatsData] = useState<IStatsCommentsData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (eventId === undefined) {
      setStatsData(null);
      return;
    }

    setLoading(true);
    getSummaryHotEventStatsComments({
      ...filterParams,
      event_id: eventId,
      page_source: 'summary',
    })
      .then((data: any) => {
        setStatsData(data || null);
      })
      .finally(() => setLoading(false));
  }, [eventId, filterParams]);

  const contentsByKind = useMemo(() => {
    if (!statsData?.contents) return EMPTY_CONTENTS_BY_KIND;
    const obj = arrayToObj(statsData.contents, 'kind');
    return {
      Positive: obj.Positive || { mentions: 0, contents: [] },
      Neutral: obj.Neutral || { mentions: 0, contents: [] },
      Negative: obj.Negative || { mentions: 0, contents: [] },
      ...obj,
    };
  }, [statsData]);

  return {
    statsData,
    contentsByKind,
    loading,
  };
}
